import { useCallback, useRef, useState } from "react";
import type { CircularCanvasLayout } from "../utils/coordinate-transform";
import { canvasToBoard } from "../utils/coordinate-transform";

interface FlickInputState {
  readonly isDragging: boolean;
  readonly startBoardX: number;
  readonly startBoardY: number;
  readonly currentBoardX: number;
  readonly currentBoardY: number;
}

interface FlickResult {
  readonly impulseX: number;
  readonly impulseY: number;
}

const POWER_SCALE = 3.0;
const MAX_DRAG_DISTANCE = 2.0;

export function useFlickInput(
  layout: CircularCanvasLayout | null,
  onFlick: (result: FlickResult) => void,
) {
  const [dragState, setDragState] = useState<FlickInputState | null>(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const onFlickRef = useRef(onFlick);
  onFlickRef.current = onFlick;

  const handlePointerDown = useCallback((canvasX: number, canvasY: number) => {
    if (!layoutRef.current) return;

    const board = canvasToBoard(canvasX, canvasY, layoutRef.current);
    setDragState({
      isDragging: true,
      startBoardX: board.x,
      startBoardY: board.y,
      currentBoardX: board.x,
      currentBoardY: board.y,
    });
  }, []);

  const handlePointerMove = useCallback((canvasX: number, canvasY: number) => {
    if (!layoutRef.current) return;

    setDragState((previousState) => {
      if (!previousState?.isDragging) return previousState;
      const activeLayout = layoutRef.current;
      if (!activeLayout) return previousState;

      const board = canvasToBoard(canvasX, canvasY, activeLayout);

      const rawDx = board.x - previousState.startBoardX;
      const rawDy = board.y - previousState.startBoardY;
      const distance = Math.sqrt(rawDx * rawDx + rawDy * rawDy);

      if (distance > MAX_DRAG_DISTANCE) {
        const scale = MAX_DRAG_DISTANCE / distance;
        return {
          ...previousState,
          currentBoardX: previousState.startBoardX + rawDx * scale,
          currentBoardY: previousState.startBoardY + rawDy * scale,
        };
      }

      return {
        ...previousState,
        currentBoardX: board.x,
        currentBoardY: board.y,
      };
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    setDragState((previousState) => {
      if (!previousState?.isDragging) return null;

      const dx = previousState.startBoardX - previousState.currentBoardX;
      const dy = previousState.startBoardY - previousState.currentBoardY;

      onFlickRef.current({
        impulseX: dx * POWER_SCALE,
        impulseY: dy * POWER_SCALE,
      });

      return null;
    });
  }, []);

  return {
    dragState,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}
